// Section 16 trust table, rationing rows, applied where the rationing decision is known:
//   rationed short while others were fed in full      -8 (announced and consistent -4; Devout -2)
//   rationed short under equal shares (or nobody full) -2
//   rationed short by a deviation from an announced order: double (-16)
//   child in household rationed short (unequally)     -12 to each adult and elder of the household
//   fed in full while others were short               +1
// Every number comes from the pipeline: num(trustChange) with the base in phases/shared/value.ts
// and modifiers from the announce policy, the Devout trait and the norms (A-06: per person per
// season the single worst rationing cause across food, water and warmth applies, not a sum).
import { clamp } from '../../core/math';
import { isChild } from '../../core/queries';
import type { HouseholdId, Person, PersonId, PhaseContext, TrustCause } from '../../core/types';
import { num } from '../shared/value';
import { EPS, type ResourcePlan } from './plan';

const UNEQUAL: readonly TrustCause[] = ['rationed-short', 'rationed-short-deviation'];

function causeFor(
  plan: ResourcePlan,
  deviated: boolean,
  announced: boolean,
  othersFull: boolean,
): TrustCause {
  if (deviated) return announced ? 'rationed-short-deviation' : 'rationed-short';
  if (plan.policy === 'equal' || !othersFull) return 'rationed-short-equal';
  return 'rationed-short';
}

export function applyRationingTrust(
  ctx: PhaseContext,
  people: readonly Person[],
  plans: readonly ResourcePlan[],
): void {
  const shortBelow = ctx.cfg.needs.shortThreshold;
  const announced = ctx.state.policies.announceRations;
  const own = new Map<PersonId, TrustCause[]>();
  const fedWhileShort = new Set<PersonId>();
  const childShort = new Set<HouseholdId>();
  for (const plan of plans) {
    const inNeed = people.filter((p) => (plan.demand[p.id] ?? 0) > 0);
    const shortIds = inNeed.filter((p) => (plan.final[p.id] ?? 1) < shortBelow);
    if (shortIds.length === 0) continue;
    const full = inNeed.filter((p) => (plan.final[p.id] ?? 1) >= 1 - EPS);
    const deviated = new Set(plan.deviated);
    for (const p of shortIds) {
      const othersFull = full.length > 0; // p is short, so every fed-in-full person is another
      const cause = causeFor(plan, deviated.has(p.id), announced, othersFull);
      own.set(p.id, [...(own.get(p.id) ?? []), cause]);
      if (isChild(ctx.cfg, p) && UNEQUAL.includes(cause)) childShort.add(p.householdId);
    }
    for (const p of full) fedWhileShort.add(p.id);
  }
  const tally = new Map<TrustCause, { delta: number; people: number }>();
  const apply = (p: Person, cause: TrustCause, delta: number): void => {
    if (delta === 0) return;
    const target = ctx.draft.people[p.id];
    if (target === undefined) return;
    target.trust = clamp(target.trust + delta, 0, ctx.cfg.people.statMax);
    const row = tally.get(cause) ?? { delta: 0, people: 0 };
    row.delta += delta;
    row.people += 1;
    tally.set(cause, row);
  };
  const value = (p: Person, cause: TrustCause): number =>
    num(ctx, { kind: 'trustChange', personId: p.id, cause });
  for (const p of people) {
    const causes = own.get(p.id);
    if (causes !== undefined) {
      let worst: { cause: TrustCause; delta: number } | null = null;
      for (const cause of new Set(causes)) {
        const delta = value(p, cause);
        if (worst === null || delta < worst.delta) worst = { cause, delta };
      }
      if (worst !== null) apply(p, worst.cause, worst.delta);
    } else if (fedWhileShort.has(p.id)) {
      apply(p, 'fed-full-while-others-short', value(p, 'fed-full-while-others-short'));
    }
    if (childShort.has(p.householdId) && !isChild(ctx.cfg, p))
      apply(p, 'child-rationed-short', value(p, 'child-rationed-short'));
  }
  for (const [cause, row] of [...tally.entries()].sort(([a], [b]) => (a < b ? -1 : 1)))
    ctx.report.add({ kind: 'trust', cause, delta: row.delta / row.people, people: row.people });
}
